import React from "react";
import { StyleSheet, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { IMediaMetadata } from "common247";
import { ThemedText } from "../ThemedText";
import { hasExtractedMetadata } from "@/services/media-metadata";

/**
 * Inline display of offline-extracted metadata within a MemoryCard.
 */
export function MemoryMetadataSection({
  metadata,
  mediaTarget,
  badgeBg,
  tintColor,
}: {
  metadata: IMediaMetadata;
  mediaTarget?: string;
  badgeBg: string;
  tintColor: string;
}) {
  if (!hasExtractedMetadata(metadata) && (!mediaTarget || mediaTarget === "Unknown")) return null;

  const capturedLabel = metadata.capturedAt
    ? new Date(metadata.capturedAt).toLocaleDateString()
    : null;

  const validGps =
    typeof metadata.gpsLatitude === "number" &&
    !isNaN(metadata.gpsLatitude) &&
    typeof metadata.gpsLongitude === "number" &&
    !isNaN(metadata.gpsLongitude);
  const locationLabel =
    metadata.gpsLocationLabel ||
    (validGps ? `${metadata.gpsLatitude!.toFixed(4)}, ${metadata.gpsLongitude!.toFixed(4)}` : null);

  const objectsFound = metadata.objects ?? [];
  const tags = metadata.tags ?? [];
  const chips = [...objectsFound, ...tags].slice(0, 8);

  return (
    <View style={styles.container}>
      <ThemedText type="stamp" style={styles.label}>
        Archival Record
      </ThemedText>

      {mediaTarget && mediaTarget !== "Unknown" ? (
        <View style={styles.row}>
          <Ionicons name="flag-outline" size={12} color={tintColor} />
          <ThemedText style={styles.rowText}>Target: {mediaTarget}</ThemedText>
        </View>
      ) : null}

      {capturedLabel ? (
        <View style={styles.row}>
          <Ionicons name="calendar-outline" size={12} color={tintColor} />
          <ThemedText style={styles.rowText}>Captured: {capturedLabel}</ThemedText>
        </View>
      ) : null}

      {locationLabel ? (
        <View style={styles.row}>
          <Ionicons name="location-outline" size={12} color={tintColor} />
          <ThemedText style={styles.rowText}>Location: {locationLabel}</ThemedText>
        </View>
      ) : null}

      {metadata.description ? (
        <View style={styles.descRow}>
          <Ionicons name="document-text-outline" size={12} color={tintColor} style={styles.descIcon} />
          <ThemedText style={styles.descText}>{metadata.description}</ThemedText>
        </View>
      ) : null}

      {chips.length > 0 ? (
        <View style={styles.objectsContainer}>
          <ThemedText style={styles.objectsLabel}>Objects found:</ThemedText>
          <View style={styles.chips}>
            {chips.map((chip, idx) => (
              <View key={`${chip}-${idx}`} style={[styles.chip, { backgroundColor: badgeBg }]}>
                <ThemedText style={[styles.chipText, { color: tintColor }]}>{chip}</ThemedText>
              </View>
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 4,
    marginBottom: 2,
    padding: 4,
    borderRadius: 0,
    backgroundColor: "rgba(255, 91, 3, 0.06)",
    borderWidth: 1,
    borderColor: "rgba(255, 91, 3, 0.2)",
    gap: 2,
  },
  label: {
    fontSize: 8.5,
    fontWeight: "bold",
    opacity: 0.4,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  rowText: {
    fontSize: 9.5,
    opacity: 0.7,
  },
  descRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 3,
    marginTop: 1,
  },
  descIcon: {
    marginTop: 1,
  },
  descText: {
    fontSize: 9.5,
    fontStyle: "italic",
    opacity: 0.85,
    flex: 1,
    lineHeight: 13,
  },
  objectsContainer: {
    marginTop: 1,
    gap: 2,
  },
  objectsLabel: {
    fontSize: 9,
    fontWeight: "600",
    opacity: 0.7,
  },
  chips: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 2,
  },
  chip: {
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  chipText: {
    fontSize: 8.5,
    fontWeight: "600",
  },
});
